<template>
  <div class="report-page-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-receipt-text-outline</v-icon>
          </div>
          <div>
            <div class="page-title">DAILY SALES REPORT</div>
            <div class="page-subtitle">Bill-wise sales summary for the selected date</div>
          </div>
        </div>

        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            :return-value.sync="date"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="date"
                label="Date"
                outlined
                background-color="white"
                readonly
                hide-details
                dense
                class="pro-input date-input"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="date"
              no-title
              scrollable
              color="#9a6b27"
              @change="$refs.menu.save(date)"
            >
              <v-spacer></v-spacer>
            </v-date-picker>
          </v-menu>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            @click="download()"
          >
            <v-icon left small>mdi-download-outline</v-icon>
            Download PDF
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- KPI Overview Cards -->
    <div class="kpi-grid mb-4">
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL BILLS</div>
          <div class="kpi-value">{{ salesRows.length }}</div>
          <div class="kpi-hint">Invoices raised on this date</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-receipt-text-outline</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL NET WEIGHT</div>
          <div class="kpi-value">{{ (totals.totalNetWt || 0).toFixed(2) }} <span class="kpi-unit">gm</span></div>
          <div class="kpi-hint">Gross {{ (totals.totalGrossWt || 0).toFixed(2) }} gm · Stone {{ (totals.totalStoneWt || 0).toFixed(2) }} gm</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
          <v-icon size="20" color="#ca8a04">mdi-gold</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL AMOUNT</div>
          <div class="kpi-value">₹{{ (totals.totalAmount || 0).toFixed(2) }}</div>
          <div class="kpi-hint">Combined sales value</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #16a34a;">
          <v-icon size="20" color="#16a34a">mdi-currency-inr</v-icon>
        </div>
      </div>
    </div>

    <!-- Data Table Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <div v-if="salesRows && salesRows.length > 0">
        <v-simple-table class="pro-master-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 120px;">Bill No</th>
                <th class="text-left">Customer</th>
                <th class="text-right" style="width: 110px;">Gross Wt</th>
                <th class="text-right" style="width: 110px;">Net Wt</th>
                <th class="text-right" style="width: 110px;">Stone Wt</th>
                <th class="text-right" style="width: 140px;">Amount</th>
                <th class="text-center" style="width: 110px;">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in salesRows" :key="i" class="master-table-row">
                <td>
                  <span class="code-pill">{{ row.billNo || '—' }}</span>
                </td>
                <td>
                  <div class="d-flex align-center">
                    <div class="supplier-avatar mr-2">
                      {{ getInitials(row.customer) }}
                    </div>
                    <div class="supplier-name-text">{{ row.customer || '—' }}</div>
                  </div>
                </td>
                <td class="text-right font-weight-medium">{{ (row.grossWeight || 0).toFixed(2) }}</td>
                <td class="text-right font-weight-semibold" style="color: #0284c7;">
                  {{ (row.netWeight || 0).toFixed(2) }}
                </td>
                <td class="text-right font-weight-medium">{{ (row.stoneWeight || 0).toFixed(2) }}</td>
                <td class="text-right font-weight-semibold" style="color: #0f172a;">
                  ₹{{ (row.amount || 0).toFixed(2) }}
                </td>
                <td class="text-center">
                  <span class="status-pill" :class="row.status === 'Paid' ? 'status-paid' : 'status-pending'">
                    {{ row.status || '—' }}
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td colspan="2" class="text-left font-weight-bold">TOTAL</td>
                <td class="text-right font-weight-bold">{{ (totals.totalGrossWt || 0).toFixed(2) }}</td>
                <td class="text-right font-weight-bold">{{ (totals.totalNetWt || 0).toFixed(2) }}</td>
                <td class="text-right font-weight-bold">{{ (totals.totalStoneWt || 0).toFixed(2) }}</td>
                <td class="text-right font-weight-bold">₹{{ (totals.totalAmount || 0).toFixed(2) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap py-12 text-center">
        <div class="empty-icon-circle mb-3">
          <v-icon size="36" color="#94a3b8">mdi-receipt-text-outline</v-icon>
        </div>
        <div class="empty-title">No sales recorded</div>
        <div class="empty-subtitle">
          No sales bills were found for the selected date.
        </div>
      </div>
    </v-card>
  </div>
</template>
<script>
import axios from "axios";
export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      menu: false,
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      salesRows: [],
      totals: { totalAmount: 0, totalGrossWt: 0, totalNetWt: 0, totalStoneWt: 0 },
    };
  },
  watch: {
    date() {
      this.getList();
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    getInitials(name) {
      if (!name) return "C";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },
    getList() {
      if (!this.date) return;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/data/dailysalesreport",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          date: this.date,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.salesRows = response.data.data.salesRows;
            this.totals = response.data.data.totals;
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    download() {
      if (!this.date) {
        this.msg = "Please choose a date";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/dailysalesreport",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          date: this.date,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "DailySalesReport.pdf");
          document.body.appendChild(link);
          link.click();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap");

.report-page-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  margin: 0 auto;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.date-input {
  width: 160px;
}

.pro-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 8px !important;
  font-size: 13px !important;
}

.pro-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.pro-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

@media (max-width: 960px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 3px 0;
  line-height: 1.2;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Data Table Card */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.pro-master-table table {
  width: 100%;
  border-collapse: collapse;
}

.pro-master-table th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.pro-master-table td {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.master-table-row:hover {
  background-color: #f8fafc !important;
}

.total-row td {
  background-color: #f8fafc !important;
  border-top: 2px solid #e2e8f0 !important;
  border-bottom: none !important;
  color: #0f172a !important;
}

.supplier-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.supplier-name-text {
  font-weight: 600;
  color: #0f172a;
  font-size: 13.5px;
}

.code-pill {
  font-family: monospace;
  font-weight: 600;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  color: #1e293b;
}

.status-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-paid {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.status-pending {
  background: #fff7ed;
  color: #ea580c;
  border: 1px solid #fed7aa;
}

/* Empty State */
.empty-state-wrap {
  padding: 60px 20px;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}
</style>
